<template>
  <div
    id="connectorView"
    class="page"
  >
    <div class="bg-white shadow-sm p-3">
      <div class="d-flex justify-content-between">
        <div class="d-flex align-items-center">
          <b-button
            id="goBack"
            variant="light"
            class="mr-3"
            @click="$router.push({name: 'connectorScreen'})"
          >
            <font-awesome-icon :icon="['fas', 'chevron-left']" />
            Back
          </b-button>
          <h6 class="m-0">
            {{ getInstanceName }}
          </h6>
        </div>
      </div>
    </div>

    <div class="connector-content">
      <div
        v-for="card in cards"
        class="connecto-card pt-3 pl-3 pr-3"
        :class="{'pb-3': card.isLast}"
      >
        <b-card
          no-body
          class="connector-card"
          :class="{'static-endpoints-card': !isPreviewCard(card)}"
        >
          <template slot="header">
            <div class="d-flex justify-content-between">
              <div
                class="header-toggle align-items-center"
                @click="onCollapseClick(card)"
              >
                <font-awesome-icon
                  v-if="card.collapsed"
                  class="mr-2"
                  :icon="['fas','chevron-down']"
                />
                <font-awesome-icon
                  v-else
                  class="mr-2"
                  :icon="['fas','chevron-up']"
                />
                <b v-if="!isPreviewCard(card)">{{ card.name }}</b>
                <b v-else>{{ card.name }}{{ previewEndpointNameSelected ? `: ${previewEndpointNameSelected}` : "" }}</b>
              </div>
              <div
                v-if="isStaticEndpointsCard(card)"
                class="search-input"
              >
                <b-form>
                  <b-form-group>
                    <b-input-group>
                      <input-widget
                        :id="`searchInputStatic`"
                        :field="searchInput"
                        :model="filterStatic"
                        @input="searchStatic"
                      />
                      <b-button
                        v-if="filterStatic.keyword"
                        id="clearSearchBtnStatic"
                        variant="transparent"
                        class="transparent search-clear-btn"
                        style="margin-left: -35px; z-index: 100;"
                        @click="resetSearchDataStatic"
                      >
                        <font-awesome-icon :icon="['fas', 'times']" />
                      </b-button>
                    </b-input-group>
                  </b-form-group>
                </b-form>
              </div>
              <div
                v-if="isEndpointsCard(card.key)"
                class="search-input"
                style="display: flex; flex-direction: column;"
              >
                <b-form style="height: 34px;">
                  <b-form-group>
                    <b-input-group>
                      <input-widget
                        :id="`searchInput`"
                        :field="searchInput"
                        :model="filter"
                        @input="search"
                      />
                      <b-button
                        v-if="filter.keyword"
                        id="clearSearchBtn"
                        variant="transparent"
                        class="transparent search-clear-btn"
                        style="margin-left: -35px; z-index: 100;"
                        @click="resetSearchData"
                      >
                        <font-awesome-icon :icon="['fas', 'times']" />
                      </b-button>
                    </b-input-group>
                  </b-form-group>
                </b-form>
                <div
                  v-if="endpointItems.length"
                  class="d-flex mt-2"
                >
                  <div class="ml-2">
                    Read
                  </div>
                </div>
              </div>
            </div>
          </template>
          <b-collapse
            :id="card.key"
            :visible="!card.collapsed"
          >
            <content-spinner
              v-if="loading || viewLoading || (disable && card.key === 'endpoints')"
              :show="loading || viewLoading || (disable && card.key === 'endpoints')"
            />

            <b-card
              v-else-if="isStaticEndpointsCard(card)"
              no-body
            >
              <b-card-body class="endpoint-content p-0">
                <no-content
                  :show="!staticEndpointsExist"
                  text="Endpoints not exist"
                />
                <connector-static-points-table
                  v-if="!loading && staticEndpointsExist"
                  ref="static-points-table"
                  :key="updateContent"
                  :items="staticEndpointItems"
                  @on-change-config-status="onStatusChange"
                />
              </b-card-body>
            </b-card>
            <b-card
              v-else-if="!!endpoints && isEndpointsCard(card.key)"
              no-body
            >
              <b-card-body
                class="endpoint-content p-0 overflow-auto"
                style="max-height: 250px;"
              >
                <no-content
                  :show="!configuredEndpointsExist"
                  :text="`Endpoints not exist`"
                />
                <connector-points-table
                  :key="updateContent"
                  :items="endpointItems"
                  :is-endpoint="true"
                  :action-buttons="actionButtons"
                  :options="{isSalesforce, isNewSalesforce: true, endpointEntryColumns: {}}"
                  @on-preview-click="onPreviewClick"
                  @on-edit-click="onEditClick"
                  @on-delete-click="onDeleteClick"
                  @onCheckboxChange="updateEndpoint"
                />
              </b-card-body>
            </b-card>
            <b-card
              v-if="card.key === 'preview'"
              no-body
            >
              <b-card-body
                class="preview-content p-0"
                style="max-height: 225px;"
              >
                <no-content
                  :show="!preview.data.length && !preview.loading && previewFetchingError"
                  :text="`Preview not exist`"
                />
                <content-spinner :show="preview.loading" />
                <b-table
                  v-if="preview.data.length > 0 && !preview.loading"
                  class="connector-table mb-0"
                  :items="preview.data"
                  :head-variant="'dark'"
                  :small="true"
                />
                <b-table
                  v-if="preview.data.length === 0 && !preview.loading && !previewFetchingError"
                  class="connector-table mb-0"
                  :items="[{Id: '', Name: '', 'Owner Id': ''}, {Id: '', Name: ' ', 'Owner Id': ' '}]"
                  :head-variant="'dark'"
                  :small="true"
                />
              </b-card-body>
            </b-card>
          </b-collapse>
        </b-card>
      </div>
    </div>

    <endpoint-config-modal @onEndpointsUpdate="setupSalesforceConnectorData" />
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import ContentSpinner from '../../core/components/ContentSpinner';
import InputWidget from '../../core/components/complex-table/components/InputWidget';
import NoContent from '../../core/components/NoContent';
import ConnectorPointsTable from './components/ConnectorPointsTable';
import ConnectorStaticPointsTable from './components/ConnectorStaticPointsTable';
import StartingPointModal from './modals/StartingPointModal';
import _ from 'lodash';
import EndpointConfigModal from '@/modules/ConnectorScreen/modals/EndpointConfigModal.vue';
import {getSalesforceNewConnectorById} from '@/store/modules/connector-screen/actions';

const {mapState, mapActions} = createNamespacedHelpers('connectorScreen');
const {mapActions: mapDataManagementActions} = createNamespacedHelpers('dataManagement');
export default {
  name: 'ConnectorViewSalesforceNew',
  components: {
    StartingPointModal,
    ConnectorPointsTable,
    NoContent,
    EndpointConfigModal,
    InputWidget,
    ContentSpinner,
    ConnectorStaticPointsTable,
  },
  data() {
    return {
      searchInput: {
        type: 'text',
        label: false,
        placeholder: 'Search',
        name: 'keyword',
      },
      filter: {
        keyword: '',
      },
      filterStatic: {
        keyword: '',
      },
      viewLoading: false,
      form: {
        username: '',
        password: '',
        accountId: '',
        token: '',
        ClientId: '',
        server: null,
        region: null,
        APIKey: '',
        ClientSecret: null,
        version: null,
        IsSandbox: false,
        AccessKey: null,
        tenant: null,
      },
      connectorCards: [
        {key: 'staticEndpoints', name: 'All Endpoints', collapsed: false},
        {key: 'endpoints', name: 'Configured Endpoints', collapsed: false},
        {key: 'preview', name: 'Preview', collapsed: true, isLast: true},
      ],
      staticEndpointItems: [],
      endpointItems: [],
      updateContent: 0,
      previewEndpointNameSelected: '',
      previewFetchingError: false,
    };
  },
  watch: {
    connector(val) {
      if (val && !!Object.keys(val).length) {
        this.endpointItems = [...this.sortEndpoints(val.endpointConfigs)];
      }
    },
  },
  computed: {
    ...mapState(['connector', 'salesforceNewConnectors', 'loading']),
    ...mapState({
      salesforceNewStaticEndpoints: state => state.salesforceNewStaticEndpoints.data,
      preview: state => state.salesforceNewPreview,
      disable: state => state.disableDuringRequest,
    }),
    staticEndpoints() {
      return this.salesforceNewStaticEndpoints.sort((a, b) => {
        const valA = a.endpointId ? 1 : 0;
        const valB = b.endpointId ? 1 : 0;

        return valA > valB ? -1 : 1;
      });
    },
    actionButtons() {
      return {
        preview: true,
        edit: true,
        delete: true,
      };
    },
    fields() {
      const fields = {};

      fields.ClientId = {
        type: 'text',
        label: 'ClientId',
        name: 'ClientId',
      };
      fields.ClientSecret = {
        type: 'password',
        label: 'Client Secret',
        name: 'ClientSecret',
      };
      fields.username = {
        type: 'text',
        label: 'Username',
        name: 'username',
      };
      fields.password = {
        type: 'password',
        label: 'Password',
        name: 'password',
      };
      fields.version = {
        type: 'text',
        label: 'Version',
        name: 'version',
      };
      fields.tenant = {
        type: 'text',
        label: 'Tenant',
        name: 'tenant',
      };
      fields.IsSandbox = {
        type: 'checkbox',
        labelPosition: 'top',
        label: 'Is Sandbox',
        name: 'IsSandbox',
      };
      if (this.form.IsSandbox) {
        fields.AccessKey = {
          type: 'text',
          label: 'AccessKey',
          name: 'AccessKey',
        };
      }

      return fields;
    },
    cards() {
      return this.connectorCards.filter(card => ['staticEndpoints', 'endpoints', 'preview'].includes(card.key));
    },
    isSalesforce() {
      return ['Salesforce', '10'].includes(this.connector.vendor) || this.connector.connectorInstanceType === 'salesforce';
    },
    endpoints() {
      if (this.connector && this.connector.endpointConfigs) {
        return this.sortEndpoints(this.connector.endpointConfigs);
      } else {
        return [];
      }
    },
    staticEndpointsExist() {
      return !!Object.keys(this.staticEndpointItems).length;
    },
    configuredEndpointsExist() {
      return !!Object.keys(this.endpointItems).length;
    },
    getInstanceName() {
      return this.connector.connectorInstanceName;
    },
  },
  methods: {
    ...mapActions([
      'getSalesforceNewConnectorById',
      'setConnectorData',
      'showEndpointModal',
      'getSalesforceEndpointPreview',
      'getSalesforceNewStaticEndpoints',
      'createSalesforceStaticEndpoint',
      'removeSalesforceStaticEndpoint',
      'updateSalesforceEndpoint',
    ]),
    onCollapseClick(card) {
      card.collapsed = !card.collapsed;
      this.$root.$emit('bv::toggle::collapse', card.key);
    },
    async onPreviewClick(item) {
      this.previewFetchingError = false;
      this.previewEndpointNameSelected = '';

      const params = {
        connectorInstanceId: this.$route.params.configurationId,
        endpointId: item.endpointId,
      };

      const previewCard = this.connectorCards.find(c => c.key === 'preview');
      previewCard.collapsed = false;
      const {success, body} = await this.getSalesforceEndpointPreview(params);

      if (success) {
        this.previewEndpointNameSelected = item.endpointName;

        if (!body.length) {
          this.$toasted.info(`There is no data for ${item.endpointName} in source system`, {
            duration: 3000,
          });
        }
        previewCard.collapsed = false;
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: 3000,
        });
        this.previewFetchingError = true;
        setTimeout(() => {
          previewCard.collapsed = true;
        }, 3000);
      }
    },
    async onStatusChange(checked, item) {
      let response;
      if (checked) {
        response = await this.createSalesforceStaticEndpoint({
          configurationId: this.$route.params.configurationId,
          endpointName: item.name,
        });

        const {success} = response;

        let newAddedEndpoint;

        if (success) {
          this.$toasted.success('Endpoint created successfully', {
            duration: TOASTER_DURATION,
          });

          newAddedEndpoint = response.body.endpointConfigs.find(ep => ep.endpointName === item.name);

          this.onEditClick(newAddedEndpoint);
        }
      } else {
        response = await this.removeSalesforceStaticEndpoint({
          configurationId: this.$route.params.configurationId,
          endpointName: item.name,
        });

        const {success} = response;

        if (success) {
          this.$toasted.success('Endpoint removed successfully', {
            duration: TOASTER_DURATION,
          });
        }
      }
    },
    resetSearchData() {
      this.endpointItems = [...this.endpoints];
      this.filter.keyword = '';
    },
    resetSearchDataStatic() {
      this.staticEndpointItems = [...this.staticEndpoints];
      this.filterStatic.keyword = '';
    },
    search(keyword) {
      if (!keyword) return this.resetSearchData();

      this.endpointItems = this.endpoints.filter(item => {
        return item.endpointName.toLowerCase().trim().indexOf(keyword.toLowerCase().trim()) > -1;
      });
    },
    searchStatic(keyword) {
      if (!keyword) return this.resetSearchDataStatic();

      this.staticEndpointItems = this.staticEndpoints.filter(item => {
        const itemName = item.originalName || item.name;
        return itemName.toLowerCase().trim().indexOf(keyword.toLowerCase().trim()) > -1;
      });
    },
    getEntityColumns(endpoint) {
      debugger;
      const endpointConfigurationJSON = endpoint.endpointConfigurationJSON;

      if (!_.isEmpty(endpointConfigurationJSON)) {
        const columnsToUpdate = JSON.parse(endpointConfigurationJSON);

        if (this.isSalesforce) {
          return columnsToUpdate ? columnsToUpdate['SalesforceColumnsToPull'] : null;
        }

        return columnsToUpdate ? columnsToUpdate['DealCloudColumnsToUpdate'] : null;
      }

      return false;
    },
    onEditClick(item) {
      this.showEndpointModal({
        ...item,
        vendor: 'Salesforce',
      });
    },
    async onDeleteClick(item) {
      item['name'] = item.endpointName;
      await this.onStatusChange(false, item);
      await this.setupSalesforceConnectorData();
      this.staticEndpointItems = [...this.staticEndpoints];
      this.endpointItems = [...this.sortEndpoints(this.endpoints)];
    },
    async onUpdateEndpoint(endpoint) {
      /*const entityColumns = this.getEntityColumns(endpoint);

      let columnsData;
      if (this.isDealCloud) {
        columnsData = {
          DealCloudColumnsToUpdate: entityColumns,
        }
      } else {
        columnsData = {
          SalesforceColumnsToPull: entityColumns,
        }
      }*/

      //const endpointConfigurationJSON = entityColumns.length ? JSON.stringify(columnsData) : null;

      const {success, body} = await this.saveEndpoint({
        data: {...endpoint, endpointConfigurationJSON: endpoint.endpointConfigurationJSON},
        action: 'update',
      });

      if (success) {
        this.$toasted.success(`Endpoint "${endpoint.friendlyName}" updated successfully`, {
          duration: TOASTER_DURATION,
        });
      } else {
        this.$toasted.error(body.ErrorMessage, {
          duration: TOASTER_DURATION,
        });
      }

      this.connector.endpoints = [...this.connector.endpoints];
      this.updateContent += 1;
    },
    isEndpointsCard(type) {
      return type === 'endpoints';
    },
    isStaticEndpointsCard(card) {
      return card.key === 'staticEndpoints';
    },
    isPreviewCard(card) {
      return card.key === 'preview';
    },
    async setupSalesforceConnectorData(updateAll = true) {
      const currentConnectorId = this.$route.params.configurationId;
      await this.getSalesforceNewConnectorById({configId: currentConnectorId});
      if (updateAll) await this.getSalesforceNewStaticEndpoints({configId: currentConnectorId});
    },
    async updateEndpoint(item) {
      const fieldToUpdate = {};
      fieldToUpdate[item.name] = item.value;
      const endpointConfig = this.connector.endpointConfigs.find(conf => conf.endpointId === item.id);

      endpointConfig[Object.keys(fieldToUpdate)[0]] = Object.values(fieldToUpdate)[0];

      const {success, body} = await this.updateSalesforceEndpoint({
        configurationId: this.$route.params.configurationId,
        body: {...endpointConfig},
      });
      if (success) {
        this.$toasted.success('Endpoint updated successfully', {
          duration: TOASTER_DURATION,
        });
        this.onHideModal();
        this.$emit('onEndpointsUpdate');
      } else {
        this.showError = true;
        this.modalError = body.ErrorMessage;
      }
    },
    sortEndpoints(endpoints) {
      return endpoints.sort((a, b) => a.endpointName.localeCompare(b.endpointName));
    },
  },
  async mounted() {
    this.viewLoading = true;

    await this.setupSalesforceConnectorData();

    this.staticEndpointItems = [...this.staticEndpoints];
    this.endpointItems = [...this.sortEndpoints(this.endpoints)];

    this.$nextTick(() => {
      this.viewLoading = false;
    });
  },
  beforeDestroy() {
    this.setConnectorData({});
  },
};
</script>

<style lang="scss">
.connector-content {
  overflow: auto;
}

.endpoint-content {
  overflow: auto;
  overflow-x: hidden;
}

.preview-content {
  overflow: auto;
}

.connector-table {
  > thead {
    > tr {
      white-space: nowrap;

      > th {
        padding: 4px 8px;
        line-height: 28px;
      }
    }
  }

  > tbody {
    > tr {
      white-space: nowrap;

      > td {
        padding: 4px 8px;
        line-height: 28px;
      }
    }
  }
}

.search-input {
  .form-group {
    margin-bottom: 0 !important;
  }
}

.static-endpoints-card .header-toggle {
  line-height: 34px;
}

.search-clear-btn {
  &:focus {
    box-shadow: none !important;
  }
}

.new-salesforce-inputs {
  display: flex;
  height: 20px;

  &>div {
    max-width: 160px;
    width: 100%;

    &:first-child {
      margin-right: 50px;
    }
    &:last-child {
      margin-right: 10px;
    }
  }

  .form-group {
    margin-bottom: 0;
  }
}

.overflow-unset {
  overflow: unset !important;
}
</style>
<style lang="scss" scoped>
::v-deep .new-salesforce-inputs {
  display: flex;
  padding-top: 0.5rem;
  align-items: center;

  .form-group {
    margin-bottom: 0;
  }
}
</style>
